// Static content for the Mirabella Anemoessa landing page.
// Owner can edit this file to update copy without touching code.

export const property = {
  name: "Mirabella Anemoessa",
  tagline: "A quiet luxury retreat by the Aegean",
  location: "Pefkochori, Halkidiki — Greece",
  shortDescription:
    "A two-bedroom luxury apartment on the first floor of a private olive-garden complex, 50 meters from the sea.",
  longAbout: `Mirabella Anemoessa is a fully equipped, air-conditioned two-bedroom apartment set inside an elegant, private complex of olive trees in the heart of Kassandra. A wide terrace overlooks a communal swimming pool, while sandy beaches, family-run tavernas and quiet bays are all within a short stroll.\n\nThe interior is designed for slow mornings and golden-hour evenings: linen textures, hand-picked ceramics, generous windows and a calm material palette inspired by the surrounding Mediterranean.`,
};

export const highlights = [
  {
    title: "Prime coastal living",
    body: "Just 50 meters from the beach and a short stroll from local tavernas and shops.",
  },
  {
    title: "Pool inside an olive garden",
    body: "Full access to a sparkling communal pool nestled in a private olive-garden complex.",
  },
  {
    title: "Spacious & fully equipped",
    body: "Two bedrooms, air-conditioning, full kitchen, and a wide terrace with pool views.",
  },
  {
    title: "Insider local knowledge",
    body: "We happily share quiet beaches, scenic trails and our favourite places across Halkidiki.",
  },
];

export const amenities = [
  { icon: "Waves", label: "Communal pool" },
  { icon: "Umbrella", label: "50m to the beach" },
  { icon: "Wind", label: "Air conditioning" },
  { icon: "Wifi", label: "High-speed Wi‑Fi" },
  { icon: "ChefHat", label: "Full kitchen" },
  { icon: "TreePalm", label: "Olive garden complex" },
  { icon: "Sun", label: "Private terrace" },
  { icon: "Tv", label: "Smart TV" },
  { icon: "WashingMachine", label: "Washing machine" },
  { icon: "Car", label: "Free parking" },
  { icon: "Coffee", label: "Espresso machine" },
  { icon: "BedDouble", label: "2 bedrooms + sofa bed · sleeps 6" },
];

export const owners = {
  title: "From your hosts",
  body: `Welcome to Mirabella Anemoessa Summer Home, your private slice of paradise in Pefkochori, Halkidiki.\n\nWe are avid global travelers who live for the magic of the great outdoors — from the soothing rhythm of the crystal-blue sea to the majestic paths of the mountains. Having explored beautiful corners across different continents, we completely fell in love with Greece's unmatched nature, golden beaches, and warm culture. When we discovered this hidden gem in the heart of Kassandra, we knew we had found something truly extraordinary.\n\nWe designed this home by combining everything we love about world-class hospitality. Now, we are thrilled to open our doors and share this beautiful, relaxing retreat directly with you.`,
  portrait:
    "https://images.unsplash.com/photo-1495567659862-0142e3b8e97b?auto=format&fit=crop&w=1200&q=80",
};

export const reviews = [
  {
    name: "Sofia M.",
    country: "Munich, Germany",
    rating: 5,
    body: "An absolute dream. The terrace at sunset is something we'll remember for years. Spotless, calm and only steps from the sea.",
    stay: "August 2024 · Family of 4",
  },
  {
    name: "Daniel & Inés",
    country: "Madrid, Spain",
    rating: 5,
    body: "The hosts shared the most beautiful quiet beach we've ever been to. The apartment itself feels boutique — textures, light, everything thought through.",
    stay: "June 2024 · Couple",
  },
  {
    name: "Theo P.",
    country: "London, UK",
    rating: 5,
    body: "Olive trees, the sound of cicadas and a perfect pool. Pefkochori has lovely tavernas and Mirabella is exactly the base you want.",
    stay: "September 2023 · Couple",
  },
  {
    name: "Elena & Marco",
    country: "Milan, Italy",
    rating: 5,
    body: "Booking was easy, the welcome was warm, and the apartment looks even better in person. We're already planning to come back next summer.",
    stay: "July 2024 · Family of 4",
  },
];

export const galleryCategories = [
  { id: "all", label: "All" },
  { id: "pool", label: "Pool & Exterior" },
  { id: "bedrooms", label: "Bedrooms" },
  { id: "bathroom", label: "Bathroom" },
  { id: "living", label: "Living & Kitchen" },
  { id: "balcony", label: "Balcony" },
  { id: "beach", label: "Beach" },
  { id: "town", label: "Town" },
];

export const galleryPlaceholders = [
  {
    url: "https://images.unsplash.com/photo-1606819422797-74627f4dac0b?auto=format&fit=crop&w=1800&q=80",
    caption: "Communal pool at golden hour",
    category: "pool",
  },
  {
    url: "https://images.unsplash.com/photo-1490465998231-e16519a88298?auto=format&fit=crop&w=1800&q=80",
    caption: "Private olive-garden complex",
    category: "pool",
  },
  {
    url: "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1800&q=80",
    caption: "The primary bedroom",
    category: "bedrooms",
  },
  {
    url: "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1800&q=80",
    caption: "Second bedroom — light & airy",
    category: "bedrooms",
  },
  {
    url: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=1800&q=80",
    caption: "Walk-in shower with natural stone",
    category: "bathroom",
  },
  {
    url: "https://images.unsplash.com/photo-1620626011761-996317b8d101?auto=format&fit=crop&w=1800&q=80",
    caption: "Crisp linen towels and Mediterranean light",
    category: "bathroom",
  },
  {
    url: "https://images.unsplash.com/photo-1527787037034-8b5b443653b7?auto=format&fit=crop&w=1800&q=80",
    caption: "Living room with Mediterranean details",
    category: "living",
  },
  {
    url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1800&q=80",
    caption: "Fully equipped open kitchen",
    category: "living",
  },
  {
    url: "https://images.unsplash.com/photo-1705268887866-e7344529b540?auto=format&fit=crop&w=1800&q=80",
    caption: "A quiet window onto the Aegean",
    category: "living",
  },
  {
    url: "https://images.unsplash.com/photo-1687956737469-7d3216f588f2?auto=format&fit=crop&w=1800&q=80",
    caption: "Halkidiki coastline — minutes from your door",
    category: "pool",
  },
];

export const heroImage =
  "https://images.unsplash.com/photo-1606819422797-74627f4dac0b?auto=format&fit=crop&w=2400&q=80";
